import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import useFetchObjects from "../../../../../api/useFetchObjects";
import { useAuth } from "../../../../../context";
import { useTranslation } from "react-i18next";
import Print from "../../../Laboratory/TestsTobeDone/Print";

const ListPatientTestReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const printRef = useRef();
  const [printData, setPrintData] = useState(null);

  const {
    data: orders,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["laboratory-test-orders"],
    `laboratory-test-orders/?doctor=${auth?.user?.user?.id}&search=${searchTerm}`,
    token,
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, refetch]);

  const handlePrint = (order) => {
    setPrintData(order);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Box>
      {/* Search Bar */}
      <Box mb={4}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {t("laboratory.labOrders")}
        </Typography>
        <TextField
          label={t("laboratory.searchPlaceholder")}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton position="start">
                <SearchIcon />
              </IconButton>
            ),
          }}
          sx={{ width: "100%", mt: 2 }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {isError && (
        <Box mt={4}>
          <Alert severity="error">{t("laboratory.errorFetchingOrders")}</Alert>
        </Box>
      )}

      {/* Laboratory Orders List */}
      {orders && orders.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{ mt: 3, boxShadow: 3, borderRadius: 2 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  #
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.patient")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.doctor")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.orderDate")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.totalAmount")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.status")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order, index) => (
                <TableRow
                  key={order.id}
                  hover
                  sx={{
                    transition: "0.3s",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    {order.patient_display?.name}
                  </TableCell>
                  <TableCell align="center">
                    {order.doctor_display?.username}
                  </TableCell>
                  <TableCell align="center">
                    {new Date(order.order_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">{order.total_amount}</TableCell>
                  <TableCell align="center">
                    {order.status === 1
                      ? t("laboratory.pending")
                      : order.status === 3
                        ? t("laboratory.completed")
                        : t("laboratory.cancelled")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("laboratory.print")}>
                      <IconButton
                        color="secondary"
                        onClick={() => handlePrint(order)}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* No Results */}
      {!isLoading && orders?.length === 0 && (
        <Box mt={4}>
          <Alert severity="info">{t("laboratory.noOrders")}</Alert>
        </Box>
      )}

      {/* Hidden Print Component */}
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <Print labTest={printData} tests={printData.tests_display} />
        </div>
      )}
    </Box>
  );
};

export default ListPatientTestReferral;
